/**
 * Work order money (BE-3a/b/c/e) — real API.
 *
 * Add-ons, fines, item payments (government fee, fine, add-on), VATP013 evidence, the disbursement review queue,
 * partner payments, and cancel / remove-line with paid-item outcomes. Backend snake_case ↔ camelCase here.
 */

import ApiService from '../api.service';
import { keysCamelToSnake, unwrapData, unwrapList } from '../../utils/apiConvert';

const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v !== undefined));
const body = (data) => keysCamelToSnake(clean(data));
const wo = (woId) => `/api/work-orders/${woId}`;
const upload = async (url, file) => {
  const form = new FormData();
  form.append('file', file);
  return unwrapData(await ApiService.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } }));
};

export const workOrderMoneyService = {
  // ── add-ons and fines ─────────────────────────────────────────────────────
  getAddons: async (woId) => unwrapList(await ApiService.get(`${wo(woId)}/addons`)).items,
  addAddon: async (woId, lineId, data) => unwrapData(await ApiService.post(`${wo(woId)}/line-items/${lineId}/addons`, body(data))),
  updateAddon: async (woId, addonId, data) => unwrapData(await ApiService.put(`${wo(woId)}/addons/${addonId}`, body(data))),
  removeAddon: async (woId, addonId) => ApiService.delete(`${wo(woId)}/addons/${addonId}`),
  setFine: async (woId, lineId, data) => unwrapData(await ApiService.put(`${wo(woId)}/line-items/${lineId}/fine`, body(data))),
  clearFine: async (woId, lineId) => ApiService.delete(`${wo(woId)}/line-items/${lineId}/fine`),
  absorbFine: async (woId, lineId) => unwrapData(await ApiService.post(`${wo(woId)}/line-items/${lineId}/fine/absorb`)),

  // ── payments ──────────────────────────────────────────────────────────────
  getPayments: async (woId) => unwrapList(await ApiService.get(`${wo(woId)}/payments`)).items,
  // data: { component, addonId?, source, sourceAccountId?, sourceCardId?, customerCardId?, paidToProviderId?, paidToName?, receiptRef?, paidAt }
  recordPayment: async (woId, lineId, data) => unwrapData(await ApiService.post(`${wo(woId)}/line-items/${lineId}/payments`, body(data))),
  reversePayment: async (woId, paymentId, reason) =>
    unwrapData(await ApiService.post(`${wo(woId)}/payments/${paymentId}/reverse`, { reason })),
  replacePayment: async (woId, paymentId, reason, payment) =>
    unwrapData(await ApiService.post(`${wo(woId)}/payments/${paymentId}/replace`, { reason, payment: body(payment) })),

  // ── evidence (VATP013) ────────────────────────────────────────────────────
  // data: { authType, receiptRef? } — classification is AGENT. The receipt file is the evidence: authorisation + file
  // is accepted (verbal waits for finance review); without the file the item shows "Evidence needed". Evidence is
  // optional: the gates check it only once started.
  setLineEvidence: async (woId, lineId, data) =>
    unwrapData(await ApiService.put(`${wo(woId)}/line-items/${lineId}/disbursement`, body({ classification: 'AGENT', ...data }))),
  setAddonEvidence: async (woId, addonId, data) =>
    unwrapData(await ApiService.put(`${wo(woId)}/addons/${addonId}/disbursement`, body({ classification: 'AGENT', ...data }))),
  // clears the authorisation, receipt number and file: the evidence is optional again
  removeLineEvidence: async (woId, lineId) => unwrapData(await ApiService.delete(`${wo(woId)}/line-items/${lineId}/disbursement`)),
  removeAddonEvidence: async (woId, addonId) => unwrapData(await ApiService.delete(`${wo(woId)}/addons/${addonId}/disbursement`)),
  uploadLineReceipt: async (woId, lineId, file) => upload(`${wo(woId)}/line-items/${lineId}/disbursement/receipt`, file),
  uploadAddonReceipt: async (woId, addonId, file) => upload(`${wo(woId)}/addons/${addonId}/disbursement/receipt`, file),
  // { downloadUrl } — 404 when no receipt is attached
  lineReceiptUrl: async (woId, lineId) => unwrapData(await ApiService.get(`${wo(woId)}/line-items/${lineId}/disbursement/receipt-url`)),
  addonReceiptUrl: async (woId, addonId) => unwrapData(await ApiService.get(`${wo(woId)}/addons/${addonId}/disbursement/receipt-url`)),

  // ── cancel / remove a line with paid items ────────────────────────────────
  // outcomes: [{ paymentId, outcome: MISTAKE|REFUNDED|ABSORBED, refundAccountId? | refundCardId? }]
  cancelWorkOrder: async (woId, reason, outcomes) =>
    unwrapData(await ApiService.post(`${wo(woId)}/cancel`, body({ reason, paidItemOutcomes: outcomes && outcomes.length ? outcomes.map(keysCamelToSnake) : undefined }))),
  removeLine: async (woId, lineId, outcomes) =>
    ApiService.delete(`${wo(woId)}/line-items/${lineId}`, outcomes && outcomes.length
      ? { data: { paid_item_outcomes: outcomes.map(keysCamelToSnake) } } : {}),

  // ── disbursement review queue ─────────────────────────────────────────────
  getReviews: async () => unwrapList(await ApiService.get('/api/disbursement-reviews')).items,
  acceptReview: async (kind, id, note) =>
    unwrapData(await ApiService.post(`/api/disbursement-reviews/${kind}/${id}/accept`, note ? { note } : {})),
  rejectReview: async (kind, id, note) =>
    unwrapData(await ApiService.post(`/api/disbursement-reviews/${kind}/${id}/reject`, note ? { note } : {})),
  reviewReceiptUrl: async (kind, id) => unwrapData(await ApiService.get(`/api/disbursement-reviews/${kind}/${id}/receipt-url`)),

  // ── partner payments (delegated work) ─────────────────────────────────────
  getPartnerPayments: async (status) =>
    unwrapList(await ApiService.get('/api/partner-payments', { params: clean({ status }) })).items,
  // target: { accountId } or { cardId }
  postPartnerPayment: async (id, target) =>
    unwrapData(await ApiService.post(`/api/partner-payments/${id}/post`, body(target))),
};

export default workOrderMoneyService;
